import dayjs, { Dayjs, ManipulateType } from 'dayjs'
import isoWeekPlugin from 'dayjs/plugin/isoWeek'
import quarterHourDayjsPlugin from './quarterHourDayjsPlugin/quarterHour'
import i18n from '../i18n'

dayjs.extend(quarterHourDayjsPlugin)
dayjs.extend(isoWeekPlugin)

const monthTranslationKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'] as const
const dayTranslationKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

/**
 * Time period resolutions supported throughout the app.
 */
export type TimePeriodResolution = 'quarterHour' | 'hour' | 'day' | 'week' | 'month' | 'year'

/**
 * Time period in terms of resolution and a single timestamp within that resolution.
 * 
 * The main upsides of using this format compared to `[from, to]` are:
 * - Integrity in terms of not allowing `[laterDate, earlierDate]`.
 * - Not having to increment two distinct variables, just do: `timestamp = timestamp.add(1, resolution)`.
 * - Being able to change resolution without having to touch the timestamp(s).
 */
export type TimePeriod = {
    resolution: TimePeriodResolution
    timestamp: Dayjs
}

/**
 * Get an unique ID for comparing if two periods are the same with different stamps.
 * 
 * @example
 * ```ts
 * const a = { resolution: 'month', timestamp: '2026-01-01' }
 * const b = { resolution: 'month', timestamp: '2026-01-02' }
 * 
 * const notSame = a.resolution === b.resolution && a.timestamp === b.timestamp
 * const same = a.resolution === b.resolution && timePeriodId(a) === timePeriodId(b)
 * ```
 * 
 * Especially useful with React dependencies:
 * ```ts
 * // Avoid:
 * const something = useMemo(() => { ... }, [timePeriod])
 * 
 * // Instead use:
 * const something = useMemo(() => { ... }, [timePeriodId(timePeriod)]
 */
export const timePeriodId = (tp: TimePeriod): string => {
    const unit = tp.resolution === 'week' ? 'isoWeek' : tp.resolution
    const start = tp.timestamp.startOf(unit as ManipulateType).toISOString()
    return `${unit}_${start}`
}

/**
 * Convert a stamped time period to a span of `[start, end]`.
 */
export const spanFromPeriod = ({ resolution, timestamp }: TimePeriod): [Dayjs, Dayjs] => {
    const unit = resolution === 'week' ? 'isoWeek' : resolution
    return [
        timestamp.startOf(unit as ManipulateType),
        timestamp.endOf(unit as ManipulateType),
    ]
}

export const isWithinPeriod = (timestamp: Dayjs, period: TimePeriod): boolean => {
    const [start, end] = spanFromPeriod(period)
    return timestamp.isBetween(start, end)
}

export const nextPeriod = ({ resolution, timestamp }: TimePeriod): TimePeriod => {
    return {
        resolution,
        timestamp: timestamp.add(1, resolution),
    }
}

export const previousPeriod = ({ resolution, timestamp }: TimePeriod): TimePeriod => {
    return {
        resolution,
        timestamp: timestamp.subtract(1, resolution),
    }
}

export const formatDateWithDynamicYear = (timestamp: Dayjs): string => {
    return timestamp.isSame(dayjs(), 'year')
        ? timestamp.format('D.M.')
        : timestamp.format('D.M.YYYY')
}

export const formatWeekWithDynamicYear = (timestamp: Dayjs): string => {
    const start = timestamp.startOf('isoWeek')
    const end = timestamp.endOf('isoWeek')
    const now = dayjs()

    const bothThisYear = start.isSame(now, 'year') && end.isSame(now, 'year')

    const format = bothThisYear ? 'D.M.' : 'D.M.YYYY'
    return `${start.format(format)} - ${end.format(format)}`
}

export const formatMonthWithDynamicYear = (timestamp: Dayjs): string => {
    const monthText = i18n.t(`months.${monthTranslationKeys[timestamp.month()]}`)
    return timestamp.isSame(dayjs(), 'year')
        ? monthText
        : `${monthText} ${timestamp.year()}`
}

export const formatWeekDay = (timestamp: Dayjs): string => {
    return i18n.t(`days.${dayTranslationKeys[timestamp.isoWeekday() - 1]}`)
}

export const formatTimePeriod = (tp: TimePeriod): string => {
    const { timestamp, resolution } = tp
    switch (resolution) {
        case 'quarterHour': {
            const minutes = Math.floor(timestamp.minute() / 15) * 15
            const minutesText = minutes.toString().padStart(2, '0')
            return `${timestamp.hour()}:${minutesText}`
        }
        case 'day':
            return formatDateWithDynamicYear(timestamp)
        case 'hour':
            return timestamp.startOf('hour').format('H:mm')
        case 'month':
            return formatMonthWithDynamicYear(timestamp)
        case 'year':
            return timestamp.format('YYYY')
        case 'week': {
            return formatWeekWithDynamicYear(timestamp)
        }
    }
}

/**
 * Similar to `formatTimePeriod`, but with some pretty exceptions like 'Today'.
 * 
 * Not as suitable for graphs, better for individual pages.
 */
export const prettyFormatTimePeriod = (tp: TimePeriod): string => {
    const timestamp = tp.timestamp.startOf(tp.resolution)
    switch (tp.resolution) {
        case 'day': {
            const daysToPresent = timestamp.diff(dayjs().startOf('day'), 'days')
            switch (daysToPresent) {
                case 1:
                    return i18n.t('tomorrow')
                case 0:
                    return i18n.t('today')
                case -1:
                    return i18n.t('yesterday')
                default: {
                    return `${formatWeekDay(timestamp)} ${formatDateWithDynamicYear(timestamp)}`
                }
            }
        }
        default:
            return formatTimePeriod(tp)
    }
}
